import type { PlatformEvent } from '../api/types.js';

let permissionRequested = false;

export async function requestPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;

  permissionRequested = true;
  return Notification.requestPermission();
}

export function hasPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

export function isPermissionRequested(): boolean {
  return permissionRequested;
}

function getTitle(event: PlatformEvent): string {
  switch (event.type) {
    case 'session.permission_pending':
      return 'Permission Required';
    case 'session.ended':
      return 'Session Ended';
    case 'cost.threshold_exceeded':
      return 'Cost Alert';
    case 'item.ready':
      return event.data.priority === 'urgent' ? 'Decision needed' : 'Ready for you';
    case 'inbox.digest':
      return typeof event.data.title === 'string' ? event.data.title : 'Decisions ready';
    default:
      return 'aidev Notification';
  }
}

function getBody(event: PlatformEvent): string {
  switch (event.type) {
    case 'session.permission_pending':
      return `Agent waiting for permission on ${event.serverId}${event.sessionId ? `/${event.sessionId.slice(0, 8)}` : ''}`;
    case 'session.ended':
      return (event.data.exitCode as number) !== 0
        ? `Session ended with error on ${event.serverId}`
        : `Session completed on ${event.serverId}`;
    case 'cost.threshold_exceeded':
      return `Monthly cost threshold exceeded: $${event.data.current}`;
    case 'item.ready':
      return typeof event.data.title === 'string' ? event.data.title : `Decision ready on ${event.serverId}`;
    case 'inbox.digest':
      return typeof event.data.body === 'string' ? event.data.body : '';
    default:
      return `Event on ${event.serverId}`;
  }
}

function getUrl(event: PlatformEvent): string {
  if ((event.type === 'item.ready' || event.type === 'inbox.digest') && typeof event.data.url === 'string' && event.data.url.startsWith('/')) return event.data.url;
  if (event.sessionId) {
    return `/servers/${event.serverId}/sessions/${event.sessionId}`;
  }
  return `/servers/${event.serverId}`;
}

const PUSH_EVENT_TYPES: PlatformEvent['type'][] = [
  'session.permission_pending',
  'session.ended',
  'cost.threshold_exceeded',
  'item.ready',
  'inbox.digest',
];

/** The operator is looking at this session right now: its page is open, in a visible and focused window */
export function watching(event: PlatformEvent, doc: Document = document, path: string = window.location.pathname): boolean {
  if (!event.sessionId) return false;
  return doc.visibilityState === 'visible' && doc.hasFocus() && path === `/servers/${event.serverId}/sessions/${event.sessionId}`;
}

export function showPushNotification(event: PlatformEvent): void {
  if (!hasPermission()) return;
  if (!PUSH_EVENT_TYPES.includes(event.type)) return;
  // Nothing to tell about the session on screen: the operator sees it happen
  if (watching(event)) return;

  // Skip session.ended with exit code 0 (normal completion); low-priority items stay on the server page
  if (event.type === 'session.ended' && (event.data.exitCode as number) === 0) return;
  // Ready items: only those the delivery policy notifies now (#760 step 3); the others wait for the digest or
  // stay on the server page, and the bell lists them all
  if (event.type === 'item.ready' && event.data.notify !== true) return;

  const notification = new Notification(getTitle(event), {
    body: getBody(event),
    // The app's icon (the browser shows its own otherwise); the badge is the small mask on mobile status bars
    icon: '/icon-192.png',
    badge: '/favicon-32x32.png',
    tag: event.type === 'item.ready' ? `aidev-item-${event.serverId}-${String(event.data.url ?? '')}` : event.type === 'inbox.digest' ? `aidev-digest-${event.serverId}` : `aidev-${event.type}-${event.serverId}`,
    requireInteraction: event.type === 'session.permission_pending' || (event.type === 'item.ready' && event.data.priority === 'urgent'),
  });

  notification.onclick = () => {
    window.focus();
    window.location.href = getUrl(event);
    notification.close();
  };
}
